<template>
  <app-modal
    v-model="isDialogOpened"
    size="lg"
    @cancel="closeDialog"
  >
    <template #title>{{ $t("project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.title") }}</template>

    <template #body>
      <div class="d-flex flex-column row-gap-4">
        <app-alert-in-page :dismissible="false" variant="info">
        <i18n-t keypath="project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.info" scope="global">
          <template #listItems>
            <ul class="my-2 tag-text-list">
              <i18n-t
                keypath="project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.infoForSignatureTag"
                tag="li"
                scope="global"
              >
                <template #signFirstNameTextTag>
                  <span class="font-weight-bold">
                    {{ $t("project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.tagText.signFirstName") }}
                  </span>
                </template>
                <template #signLastNameTextTag>
                  <span class="font-weight-bold">
                    {{ $t("project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.tagText.signLastName") }}
                  </span>
                </template>
              </i18n-t>
              <i18n-t
                keypath="project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.infoForSignatureDateTag"
                tag="li"
                scope="global"
              >
                <template #dateFirstNameTextTag>
                  <span class="font-weight-bold">
                    {{ $t("project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.tagText.dateFirstName") }}
                  </span>
                </template>
                <template #dateLastNameTextTag>
                  <span class="font-weight-bold">
                    {{ $t("project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.tagText.dateLastName") }}
                  </span>
                </template>
              </i18n-t>
            </ul>
          </template>
          <template #firstThreeCharacters>
            <span class="font-weight-bold">
              {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.firstThreeCharacters') }}
            </span>
          </template>
          <template #tagTextExample>
            <p class="mt-2 font-italic">
              {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.tagTextExample') }}
            </p>
          </template>
        </i18n-t>
        <AppDownloadFile :link="autoTagsInfosLink">
          <template #default="{ submitForm }">
            <a class="app-link learnMoreLink" @click="submitForm">
              {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.moreInfos') }}
              <app-material-icon name="open_in_new" color="icon-link" size="xs" />
            </a>
          </template>
        </AppDownloadFile>
      </app-alert-in-page>
      <template v-if="!loading && wordsFound.length !== 0">
        <app-alert-in-page variant="warning" v-show="!isFormValid">
          {{ $t("project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.warning") }}
        </app-alert-in-page>
        <div class="tags-found-container">
          <p class="text--small">{{ $t("project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.text") }}</p>
          <AppSelect v-for="tag in wordsFound" :key="tag.text"
                                               :id="tag.text"
                                               v-model="tag.user"
                                               item-title="text"
                                               item-value="value"
                                               :items="knownUsersOptions"
                                               :label="tag.text"
          />
        </div>
      </template>
      <template v-if="!loading && wordsFound.length === 0">
        <p class="mt-4 text-center text--medium">
          {{ $t("project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.emptyText") }}
        </p>
      </template>
      <template v-if="loading">
        <v-progress-circular
          indeterminate
          size="32"
          style="width: 3rem; height: 3rem; margin: 100px auto"
        />
      </template>
      </div>
    </template>

    <template #footer>
      <v-spacer />
      <app-button
        class="mr-2"
        type="outlined"
        variant="neutral"
        @click="closeDialog"
      >
        {{ $t("common.cancel") }}
      </app-button>
      <AppTooltip :disabled="isFormValid" location="bottom">
        <template v-slot:activator="{ props }">
            <span v-bind="props">
              <app-button
                :disabled="!isFormValid || loading"
                @click="wordsFound.length === 0 ? closeDialog() : confirmAutoTags()"
              >
                {{ $t("common.confirm") }}
              </app-button>
            </span>
        </template>
        <p>{{$t('project.signing-checklist.prepare-signature.PrepareSignatureAutoTagsDialog.confirmTooltip')}}</p>
      </AppTooltip>
    </template>
  </app-modal>
</template>

<script>
import { uniqBy, sortBy } from 'lodash-es'
import { defineComponent } from 'vue'
import { mapGetters, mapActions, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { dialogMixin } from '@/mixins/dialogMixin'
import { GET_AUTO_TAGS, GET_TAGGING_INFORMATION, POST_AUTO_TAGS } from '@/store/modules/prepareSignature/action_types'

export default defineComponent({
  name: 'PrepareSignatureAutoTagsDialog',
  components: {
    AppSelect,
    AppTooltip,
    AppDownloadFile,
  },
  mixins: [dialogMixin],
  data () {
    return {
      // Words found are based on the tags found with a unicity based on the word.
      // This is shown to the user to confirm the related users for each unique word
      wordsFound: [],
      // This is the raw tags found from the back-end response
      tagsFound: [],
      loading: true,
      knownUsers: [],
      knownUsersOptions: [],
    }
  },
  computed: {
    ...mapState('prepareSignature', ['envelopeDisplayInformation']),
    ...mapGetters('room', ['roomMnemo']),
    isFormValid () {
      return this.wordsFound.every((tag) => Number.isInteger(tag.user))
    },
    autoTagsInfosLink () {
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=auto-tags-${this.$i18n.locale.toUpperCase()}`
    },
  },
  async created () {
    try {
      const taggingInfo = await this.GET_TAGGING_INFORMATION({
        mnemo: this.roomMnemo,
        envId: this.envelopeDisplayInformation.envelopeId,
      })
      this.knownUsers = taggingInfo.signers.flat()
      this.knownUsersOptions = this.formatKnownUsers(this.knownUsers)

      this.tagsFound = await this.GET_AUTO_TAGS({
        mnemo: this.roomMnemo,
        envId: this.envelopeDisplayInformation.envelopeId,
      })
      this.wordsFound = uniqBy(this.tagsFound, 'word').map((tag) => {
        const extractedWord = tag.word.substring(7, tag.word.length - 2)
        const user = this.findMatchingUser(extractedWord, true)
        const userId = (user && user.signerId) || ''
        return {
          text: tag.word,
          user: userId,
        }
      })
    } catch (error) {
      console.error(error)
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...mapActions('prepareSignature', [GET_TAGGING_INFORMATION, GET_AUTO_TAGS, POST_AUTO_TAGS]),
    async confirmAutoTags () {
      this.loading = true
      const formattedData = {}
      this.wordsFound.forEach((tag) => {
        formattedData[tag.text] = tag.user
      })
      try {
        await this.POST_AUTO_TAGS({
          mnemo: this.roomMnemo,
          envId: this.envelopeDisplayInformation.envelopeId,
          data: formattedData,
        })
        this.$emit('get-tags')
        // We may get pages out of order here, so we re-sort them because getpage needs them in order.
        // We need to do that to scroll to the first one later anyway so that's all good :)
        const sortedUniqueTagPages = sortBy(uniqBy(this.tagsFound, 'page'), ['page']).map((tagPage) => tagPage.page)
        this.$emit('get-pages', { pages: sortedUniqueTagPages })
        this.closeDialog()
        this.$emit('scroll-to', sortedUniqueTagPages[0])
      } finally {
        this.loading = false
      }
    },
    formatKnownUsers (knownUsers) {
      return knownUsers.map((user) => (
        {
          text: user.fullName,
          value: user.signerId,
        }
      ))
    },

    /**
     * Find a user from the known users. If there are multiple matches, they are ignored and this returns nothing.
     * @param {string} word The string to use to find the user
     * @param {boolean} shallow Determines if the search should be done with an exact match of the fullname or not.
     *                          If it's not, it will match any word that starts the same as
     *                          either the name or lastName of a user.
     * @returns {object|undefined}
     */
    findMatchingUser (word, shallow = true) {
      let userMatch = []
      if (shallow) {
        userMatch = this.knownUsers.filter(
          (user) => (
            this.normalizeWord(user.firstName).startsWith(this.normalizeWord(word)) ||
            this.normalizeWord(user.lastName).startsWith(this.normalizeWord(word))
          ),
        )
      } else {
        userMatch = this.knownUsers.filter(
          (user) => (
            user.fullName.toLowerCase() === word.toLowerCase()
          ),
        )
      }
      if (userMatch.length === 0 || userMatch.length > 1) {
        return undefined
      } else {
        return userMatch[0]
      }
    },
    /**
     * Returns a normalized version of a word, without diacritics and accents, in all lowercase
     * @param {string} word The word source
     * @returns {string} The same word, normalized
     */
    normalizeWord (word) {
      return word.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },
  },
})
</script>

<style scoped lang="scss">
.tags-found-container {
  display: flex;
  flex-direction: column;
  max-height: 250px;
  overflow-y: auto;
  row-gap: 16px;
}

.tag-text-list {
  list-style: '-';
  padding-left: 8px;

  li {
    padding-left: 8px;
    margin-block: 4px;
  }
}
</style>
